import React from 'react';
import {
  Box,
  Button,
  Collapse,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';

/**
 * One monthly metric column rendered with `display: contents` so the parent
 * CSS grid can equalize card heights across columns (title / card / chart rows).
 */
export default function MonthlyMetricColumn({
  sectionTitle,
  card,
  chart,
  chartKey,
  columnIndex,
  columnCount,
  graphsExpanded,
  onToggleGraphs,
  isMobile,
  chartsLoading,
}) {
  const { t } = useTranslation();
  const mdCol = Math.min(columnIndex + 1, columnCount);

  const titleSx = {
    gridColumn: { xs: '1', md: String(mdCol) },
    gridRow: { md: '1' },
    fontWeight: 600,
    fontSize: { xs: '1rem', sm: '1.1rem' },
    alignSelf: 'end',
  };

  const cardSx = {
    gridColumn: { xs: '1', md: String(mdCol) },
    gridRow: { md: '2' },
    height: { md: '100%' },
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  };

  const chartSx = {
    gridColumn: { xs: '1', md: String(mdCol) },
    gridRow: { md: '3' },
    alignSelf: 'start',
    width: '100%',
  };

  return (
    <>
      <Typography
        variant="subtitle1"
        component="h3"
        sx={titleSx}
        data-testid={`dashboard-monthly-column-${chartKey}`}
      >
        {sectionTitle}
      </Typography>

      <Box sx={cardSx}>
        {card}
      </Box>

      {!chartsLoading && chart && (
        isMobile ? (
          <Box sx={{ ...chartSx, mt: { xs: 0 } }}>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              onClick={onToggleGraphs}
              endIcon={graphsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              data-testid={`dashboard-toggle-graphs-${chartKey}`}
              aria-expanded={graphsExpanded}
            >
              {graphsExpanded
                ? t('dashboard.monthly.hideGraphs')
                : t('dashboard.monthly.showGraphs')}
            </Button>
            <Collapse in={graphsExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ mt: 1 }}>
                {chart}
              </Box>
            </Collapse>
          </Box>
        ) : (
          <Box sx={chartSx}>
            {chart}
          </Box>
        )
      )}
    </>
  );
}
